import { useQuery } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'

import { PmsLookupProvider } from './pms.constants'

export interface PmsProperty {
  id: string | number
  name: string
}

export const USE_GET_PMS_PROPERTIES_QUERY_KEY = 'useGetPmsProperties'
export const USE_GET_PMS_PROPERTY_QUERY_KEY = 'useGetPmsProperty'

const MANAGEMENT_PARAM: Record<PmsLookupProvider, string> = {
  entrata: 'entrataManagementId',
  rentManager: 'rentManagerManagementId',
  yardi: 'yardiManagementId',
}

const PROPERTY_PARAM: Record<PmsLookupProvider, string> = {
  entrata: 'entrataPropertyId',
  rentManager: 'rentManagerPropertyId',
  yardi: 'yardiPropertyId',
}

const LIST_ROUTE: Record<PmsLookupProvider, string> = {
  entrata: '/entrata/properties',
  rentManager: '/rent-manager/properties',
  yardi: '/v2/yardi/properties',
}

const ONE_ROUTE: Record<PmsLookupProvider, string> = {
  entrata: '/v2/entrata/property',
  rentManager: '/rent-manager/property',
  yardi: '/v2/yardi/property',
}

interface RawProperty {
  entrataPropertyId?: string | number
  id: string | number
  name: string
}

export const getPmsProperties = async (
  provider: PmsLookupProvider,
  companyId: number,
  signal?: AbortSignal,
): Promise<PmsProperty[]> => {
  const response = await authAPI.get<RawProperty[]>(LIST_ROUTE[provider], {
    params: { [MANAGEMENT_PARAM[provider]]: companyId },
    signal,
  })

  // Note: Entrata lists its own property id, not the row id
  return response.data.map((property) => ({
    id: provider === 'entrata' ? (property.entrataPropertyId ?? property.id) : property.id,
    name: property.name,
  }))
}

export const getPmsProperty = async (
  provider: PmsLookupProvider,
  companyId: number,
  propertyId: string | number,
  signal?: AbortSignal,
): Promise<PmsProperty> => {
  const response = await authAPI.get<RawProperty>(ONE_ROUTE[provider], {
    params: { [MANAGEMENT_PARAM[provider]]: companyId, [PROPERTY_PARAM[provider]]: propertyId },
    signal,
  })
  return { id: response.data.id, name: response.data.name }
}

export const useGetPmsProperties = (
  provider: PmsLookupProvider | null | undefined,
  companyId: number | null | undefined,
) =>
  useQuery({
    enabled: !!provider && !!companyId,
    queryFn: ({ signal }) =>
      getPmsProperties(provider as PmsLookupProvider, companyId as number, signal),
    queryKey: [USE_GET_PMS_PROPERTIES_QUERY_KEY, provider, companyId],
  })

export const useGetPmsProperty = (
  provider: PmsLookupProvider | null | undefined,
  companyId: number | string | null | undefined,
  propertyId: string | number | null | undefined,
) =>
  useQuery({
    enabled: !!provider && !!companyId && !!propertyId,
    queryFn: ({ signal }) =>
      getPmsProperty(
        provider as PmsLookupProvider,
        Number(companyId),
        propertyId as string | number,
        signal,
      ),
    queryKey: [USE_GET_PMS_PROPERTY_QUERY_KEY, provider, companyId, propertyId],
  })
